import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import doctorService, {
  Doctor,
  DoctorSearchParams,
  TemplateResolveResponse,
} from '../services/doctorService';

// ── State ────────────────────────────────────────────────────────────────

export interface DoctorState {
  searchResults: Doctor[];
  searchTotal: number;
  searchLoading: boolean;
  searchError: string | null;

  selectedDoctor: Doctor | null;

  resolvedTemplate: TemplateResolveResponse | null;
  resolvingTemplate: boolean;
  resolveError: string | null;
}

const initialState: DoctorState = {
  searchResults: [],
  searchTotal: 0,
  searchLoading: false,
  searchError: null,
  selectedDoctor: null,
  resolvedTemplate: null,
  resolvingTemplate: false,
  resolveError: null,
};

// ── Async Thunks ─────────────────────────────────────────────────────────

export const searchDoctors = createAsyncThunk(
  'doctors/search',
  async (params: DoctorSearchParams) => {
    return await doctorService.searchDoctors(params);
  },
);

export const resolveDoctorTemplate = createAsyncThunk(
  'doctors/resolveTemplate',
  async ({
    doctorId,
    cptCode,
    modality,
    bodyRegion,
  }: {
    doctorId: number;
    cptCode?: string;
    modality?: string;
    bodyRegion?: string;
  }) => {
    return await doctorService.resolveTemplate(doctorId, {
      cpt_code: cptCode,
      modality,
      body_region: bodyRegion,
    });
  },
);

// ── Slice ────────────────────────────────────────────────────────────────

const doctorSlice = createSlice({
  name: 'doctors',
  initialState,
  reducers: {
    selectDoctor(state, action: PayloadAction<Doctor | null>) {
      state.selectedDoctor = action.payload;
      if (!action.payload) {
        state.resolvedTemplate = null;
      }
    },
    clearDoctorSearch(state) {
      state.searchResults = [];
      state.searchTotal = 0;
      state.searchError = null;
    },
    clearResolvedTemplate(state) {
      state.resolvedTemplate = null;
      state.resolveError = null;
    },
    resetDoctorState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchDoctors.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchDoctors.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.items;
        state.searchTotal = action.payload.total;
      })
      .addCase(searchDoctors.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.error.message || 'Search failed';
      })
      .addCase(resolveDoctorTemplate.pending, (state) => {
        state.resolvingTemplate = true;
        state.resolveError = null;
      })
      .addCase(resolveDoctorTemplate.fulfilled, (state, action) => {
        state.resolvingTemplate = false;
        state.resolvedTemplate = action.payload;
      })
      .addCase(resolveDoctorTemplate.rejected, (state, action) => {
        state.resolvingTemplate = false;
        state.resolveError = action.error.message || 'Template resolution failed';
      });
  },
});

export const {
  selectDoctor,
  clearDoctorSearch,
  clearResolvedTemplate,
  resetDoctorState,
} = doctorSlice.actions;
export default doctorSlice.reducer;
